import { queryOptions } from '@tanstack/react-query'

import { apiClient } from './client'
import type { ArchivedDocument, DocumentKind, DocumentPage } from './documents-types'

/**
 * Downloads for the printed paper documents the institution runs on: field collection sheets,
 * teller receipts, savings and loan statements, and a loan's pre-agreement disclosure — and the
 * archive every one of them is filed in once generated.
 *
 * Every function here follows `reports-api.ts`'s `exportReport`: a plain `apiClient.get` with
 * `responseType: 'blob'`, so the same interceptor that attaches the bearer token and branch header
 * to a JSON request attaches them here too — there is no second, unauthenticated path to any of
 * these endpoints. The server names the file in `Content-Disposition`; a stable fallback name covers
 * the rare case that header is missing.
 */

/** Query-string parameters, with the members nobody chose removed. */
const params = (values: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  )

/** The server names the file in `Content-Disposition`; `fallback` covers the rare case it is absent. */
function filenameFromDownload(headers: Record<string, unknown>, fallback: string): string {
  const disposition = headers['content-disposition']

  if (typeof disposition === 'string') {
    const match = /filename\*?=(?:UTF-8'')?"?([^;"]+)/i.exec(disposition)
    if (match?.[1]) {
      try {
        return decodeURIComponent(match[1])
      } catch {
        return match[1]
      }
    }
  }

  return fallback
}

async function download(
  path: string,
  fallbackFilename: string,
  query?: Record<string, unknown>,
): Promise<{ blob: Blob; filename: string }> {
  const response = await apiClient.get<Blob>(path, {
    params: query ? params(query) : undefined,
    responseType: 'blob',
  })

  return { blob: response.data, filename: filenameFromDownload(response.headers, fallbackFilename) }
}

/**
 * The pre-printed field collection sheet for one scheduled group meeting.
 *
 * `GET /meetings/{meetingId}/collection-sheet`, under `groups.view` — the same read that already
 * covers seeing the meeting and its roster.
 */
export function downloadCollectionSheet(meetingId: string): Promise<{ blob: Blob; filename: string }> {
  return download(`/meetings/${encodeURIComponent(meetingId)}/collection-sheet`, `collection-sheet-${meetingId}.pdf`)
}

/**
 * A savings account statement — the same movements and running balance
 * `savingsStatementQueryOptions` serves, laid out for printing.
 *
 * `GET /savings/accounts/{accountId}/statement`, under `savings.statement.view`.
 */
export function downloadSavingsStatement(
  accountId: string,
  query: { from?: string; to?: string } = {},
): Promise<{ blob: Blob; filename: string }> {
  return download(
    `/savings/accounts/${encodeURIComponent(accountId)}/statement`,
    `savings-statement-${accountId}.pdf`,
    query,
  )
}

/**
 * The teller receipt for one posted savings transaction.
 *
 * `GET /savings/accounts/{accountId}/transactions/{transactionId}/receipt`, under
 * `savings.statement.view` — a teller who can see the transaction can print its receipt.
 */
export function downloadSavingsTransactionReceipt(
  accountId: string,
  transactionId: string,
): Promise<{ blob: Blob; filename: string }> {
  return download(
    `/savings/accounts/${encodeURIComponent(accountId)}/transactions/${encodeURIComponent(transactionId)}/receipt`,
    `receipt-${transactionId}.pdf`,
  )
}

/**
 * A loan round's statement — schedule against paid, arrears, and outstanding.
 *
 * `GET /loan-cycles/{cycleId}/statement`, under `loans.statement.view`.
 */
export function downloadLoanStatement(cycleId: string): Promise<{ blob: Blob; filename: string }> {
  return download(`/loan-cycles/${encodeURIComponent(cycleId)}/statement`, `loan-statement-${cycleId}.pdf`)
}

/**
 * The teller receipt for one posted loan transaction, most often a repayment.
 *
 * `GET /loan-cycles/{cycleId}/transactions/{transactionId}/receipt`, under `loans.statement.view`.
 */
export function downloadLoanTransactionReceipt(
  cycleId: string,
  transactionId: string,
): Promise<{ blob: Blob; filename: string }> {
  return download(
    `/loan-cycles/${encodeURIComponent(cycleId)}/transactions/${encodeURIComponent(transactionId)}/receipt`,
    `receipt-${transactionId}.pdf`,
  )
}

/**
 * The pre-agreement disclosure and key facts statement for an approved round, before disbursement.
 * Marked DRAFT — pending compliance review — on the document itself.
 *
 * `GET /loan-cycles/{cycleId}/disclosure`, under `loans.disclosure.generate`. Refused once a round
 * has moved past Approved/PendingDisbursement: the round's own statement is the record that governs
 * from disbursement onward.
 */
export function downloadLoanDisclosure(cycleId: string): Promise<{ blob: Blob; filename: string }> {
  return download(`/loan-cycles/${encodeURIComponent(cycleId)}/disclosure`, `loan-disclosure-${cycleId}.pdf`)
}

// ----------------------------------------------------------------------------------- the archive

export interface ArchivedDocumentsQuery {
  kind?: DocumentKind
  branchId?: string
  entityReference?: string
  search?: string
  from?: string
  to?: string
  page?: number
  pageSize?: number
}

/**
 * The archive: every document generated, newest first, scoped by the server to the branches the
 * caller can reach.
 *
 * `GET /documents`, under `documents.view`. Short stale time: this is the screen a teller opens to
 * reprint what was just handed over, and a list from a minute ago may not have it yet.
 */
export const archivedDocumentsQueryOptions = (query: ArchivedDocumentsQuery = {}) =>
  queryOptions({
    queryKey: ['documents', 'archive', query] as const,
    queryFn: async () => {
      const response = await apiClient.get<DocumentPage>('/documents', {
        params: params({
          kind: query.kind,
          branchId: query.branchId,
          entityReference: query.entityReference,
          search: query.search,
          from: query.from,
          to: query.to,
          page: query.page ?? 1,
          pageSize: query.pageSize ?? 25,
        }),
      })

      const data = response.data
      return {
        items: Array.isArray(data?.items) ? data.items : [],
        page: Number(data?.page ?? 1),
        pageSize: Number(data?.pageSize ?? 25),
        totalCount: Number(data?.totalCount ?? 0),
      } satisfies DocumentPage
    },
    staleTime: 10 * 1000,
  })

/**
 * The archived bytes of one document, exactly as they were handed over.
 *
 * `GET /documents/{id}/content`, under `documents.view`.
 */
export function downloadArchivedDocument(
  document: Pick<ArchivedDocument, 'id' | 'fileName'>,
): Promise<{ blob: Blob; filename: string }> {
  return download(`/documents/${encodeURIComponent(document.id)}/content`, document.fileName)
}
